<template>
	<div v-if="loaded">
		<timezone @change="zoneChanged" />

		<counts-daily @day-click="yearDayClick" :region="filter.region" />

		<counts-hourly :date="chartDay" @hour-click="hourClick" :region="filter.region" />

		<v-card class="mb-3">
			<v-card-text>
				<v-row>
					<v-col class="mr-1 shrink" cols="2" >
						<v-dialog
							v-model="startMenu"
							persistent
							width="640px"
						>
							<template v-slot:activator="{ on }">
								<v-text-field
									:value="startDate"
									label="Start Date / Time"
									prepend-icon="far fa-calendar-alt"
									readonly
									v-on="on"
								></v-text-field>
							</template>
							<v-card class="pt-3">
								<v-card-text>
									<v-row no-gutters class="flex-nowrap">
										<v-col>
											<v-date-picker v-model="filter.dateIso" scrollable class="double-picker">
											</v-date-picker>
										</v-col>
										<v-col>
											<v-time-picker v-model="filter.timeIso">
											</v-time-picker>
										</v-col>
									</v-row>
								</v-card-text>
								<v-card-actions>
									<v-spacer></v-spacer>
									<v-btn color="blue darken-1" text @click="startChanged">Close</v-btn>
								</v-card-actions>
							</v-card>
						</v-dialog>
					</v-col>
					<v-col class="shrink" cols="2" >
						<v-btn outlined color="primary" style="margin-top:10px;" @click="nowClick"><v-icon small class="mr-2">far fa-clock</v-icon> Now</v-btn>
					</v-col>
					<v-col class="pr-5 shrink" cols="2" >
						<v-select persistent-hint v-model.number="filter.minutes" label="Minutes" :items="[10,30,60]" @change="filterChanged"></v-select>
					</v-col>
					<v-col class="pr-5 shrink" cols="2" >
						<v-select persistent-hint v-model="filter.region" label="Region" :items="regions" @change="filterChanged"></v-select>
					</v-col>
					<v-col class="grow" ></v-col>
				</v-row>
			</v-card-text>
		</v-card>

		<v-card flat class="mb-3">
			<v-toolbar flat dense color="primary">
				<v-btn icon @click="goBack" color="white" small>
					<v-icon small>far fa-arrow-to-left</v-icon>
				</v-btn>
				<v-toolbar-title class="title white--text" style="margin: 0 10px">Requests</v-toolbar-title>

				<v-icon color="white" v-if="refreshing">fa-spin fa-spinner</v-icon>

				<v-spacer></v-spacer>
				<v-btn icon @click="goForward" color="white" small>
					<v-icon small>far fa-arrow-to-right</v-icon>
				</v-btn>
			</v-toolbar>

			<v-divider></v-divider>

			<v-card-text>
				<div class="status-grid" :class="`span-${filter.minutes}`">
					<div class="times">
						<div v-for="t in times" :key="t.id">{{t.time}}</div>
					</div>
					<div class="counts">
						<div v-for="c in queueCounts" :key="c.id">{{c.count}}</div>
					</div>
					<div class="lined-section">
						<div class="lines">
							<div v-for="i in new Array(filter.minutes)" :key="i"></div>
						</div>
						<div class="items">
							<request-marker v-for="t in transactionItems" :key="`t-item-${t.id}`" :item="t" @click="requestClick" />
						</div>
					</div>
				</div>
			</v-card-text>
		</v-card>

		<v-card flat class="mb-3">
			<v-toolbar flat dense color="primary">
				<v-btn icon @click="goBack" color="white" small>
					<v-icon small>far fa-arrow-to-left</v-icon>
				</v-btn>
				<v-toolbar-title class="title white--text" style="margin: 0 10px">Workers</v-toolbar-title>

				<v-icon color="white" v-if="refreshing">fa-spin fa-spinner</v-icon>

				<v-spacer></v-spacer>
				<v-btn icon @click="goForward" color="white" small>
					<v-icon small>far fa-arrow-to-right</v-icon>
				</v-btn>
			</v-toolbar>

			<v-divider></v-divider>

			<v-card-text>
				<div class="status-grid" :class="`span-${filter.minutes}`">
					<div class="times">
						<div v-for="t in times" :key="t.id">{{t.time}}</div>
					</div>
					<div class="counts">
						<div v-for="c in workerMarkers" :key="c.id">{{c.count}}</div>
					</div>
					<div class="lined-section">
						<div class="lines">
							<div v-for="i in new Array(filter.minutes)" :key="i"></div>
						</div>
						<div class="items">
							<system-status-worker v-for="t in workerItems" :key="`w-item-${t.id}`" :item="t" @click="requestClick" />
						</div>

					</div>
				</div>
			</v-card-text>
		</v-card>
		<transaction-modal :tracking-number="trackingNumber" @view="viewTrackingNumber" />
	</div>
</template>

<script>
import { DateTime, Interval } from "luxon";
import resize from "vue-resize-directive";
import api from "../api";
import { transactionStatus } from "../enums";
import requestMarker from "../components/system-status/system-status-request.vue";
import systemStatusWorker from "../components/system-status/system-status-worker.vue";
import countsDaily from "../components/system-status/counts-daily.vue";
import countsHourly from "../components/system-status/counts-hourly.vue";
import transactionModal from "../components/transaction-modal.vue";
import timezone	from "../components/timezone.vue";

export default {
	components: {
		requestMarker,
		countsDaily,
		countsHourly,
		systemStatusWorker,
		transactionModal,
		timezone,
	},
	directives: {
		resize,
	},
	data() {
		return {
			filter: {
				dateIso: null,
				timeIso: null,
				minutes: null,
				region: "",
			},
			chartDay: null,
			startM: null,
			visibleTimeMin: 60,	// visible time period
			autoRefresh: false,
			intervalTimer: null,
			refreshing: false,
			requests: [],	// compositerequest
			workers: [],
			selectedRequest: null,
			// computation and view state
			loaded: false,
			startMenu: false,
			request: null,
			transaction: null,
			trackingNumber: null,
		};
	},
	computed: {
		regions() {
			return ["", ...this.config.lists.requestRegions];
		},
		times() {
			const markers = [];
			for (let i = 0; i < this.filter.minutes; i += 1) {
				const thisTime = this.startM.plus({ minutes: i });
				markers.push({
					id: `t-${i}`,
					timeM: thisTime,
					time: thisTime.toLocaleString(DateTime.TIME_24_SIMPLE),
				});
			}
			return markers;
		},
		queueCounts() {
			return this.times.map((t, i) => ({
				id: `qc-${i}`,
				count: this.transactionItems.filter(r => Interval.fromDateTimes(t.timeM, t.timeM.plus({ minutes: 1 })).contains(r.start)).length,
			}));
		},
		// display items created from requests
		transactionItems() {
			return this.requests.map((i) => {
				const leftCalc = this.timeToPct(i.created);
				const dqLeftCal = this.timeToPct(i.jobStarted);
				const widthCalc = this.spanToPct(i.created, i.jobFinished);
				return {
					id: i.id,
					left: leftCalc.value,
					width: widthCalc.value,
					dqLeft: dqLeftCal.value,
					jobWidth: widthCalc.value - (dqLeftCal.value - leftCalc.value),
					status: this.statusMap(i.status),
					dqOut: dqLeftCal.outStart || dqLeftCal.outEnd,
					startOut: leftCalc.outStart,
					endOut: widthCalc.outEnd,
					workerId: i.workerId,
					start: i.jobStarted,	// local
				};
			}).filter(i => i.width > 0);
		},
		// display items for workers
		workerItems() {
			// todo, show shutdown request, like dq marker
			return this.workers.map((w) => {
				const leftCalc = this.timeToPct(w.created);
				const widthCalc = this.spanToPct(w.created, w.offline);
				return {
					id: w.id,
					created: w.created,
					offline: w.offline,
					status: w.status,
					left: leftCalc.value,
					startOut: leftCalc.outStart,
					endOut: widthCalc.outEnd,
					width: widthCalc.value,
					jobs: this.transactionItems.filter(r => r.workerId === w.id),
				};
			});
		},
		// display the count of workers per time interval
		workerMarkers() {
			return this.times.map((t, i) => ({
				id: `wc-${i}`,
				count: this.workerItems.filter(w => Interval.fromDateTimes(w.created, w.offline ? w.offline : this.dtnow()).contains(t.timeM)).length,
			}));
		},
		startDate() {
			return `${this.filter.dateIso} ${this.filter.timeIso}`;
		},
		startDateM() {
			return DateTime.fromISO(`${this.filter.dateIso}T${this.filter.timeIso}`);
		},
	},
	created() {
		const startParam = this.$route.params.start;
		const now = this.dtnow();

		let start = startParam ? this.dt(`${this.$route.params.start}`) : now;

		// round back to the last hour
		start = start.set({ second: 0, millisecond: 0, minute: 0 });

		this.filter.dateIso = start.toISODate();
		this.filter.timeIso = start.toLocaleString(DateTime.TIME_24_SIMPLE);
		this.filter.minutes = 60;
		this.chartDay = start;
		this.loadStatus();
	},
	watch: {
		autoRefresh(v) {
			if (v) {
				this.startInterval();
			} else {
				this.stopInterval();
			}
		},
	},
	methods: {
		timeToPct(m) {
			if (!m) {
				return {
					value: null,
					outStart: true,
					outEnd: true,
				};
			}

			let diffMs = Interval.fromDateTimes(this.startM, m).length();
			let outStart = false;

			// m before start
			if (Number.isNaN(diffMs) || diffMs < 0) {
				diffMs = 0;
				outStart = true;
			}

			const totalMs = this.visibleTimeMin * 60 * 1000;
			let pct = 100 * diffMs / totalMs;

			const outEnd = pct > 100;
			if (outEnd) {
				pct = 100;
			}
			if (outStart) {
				pct = 0;
			}
			return {
				value: pct,
				outStart,
				outEnd,
			};
		},
		spanToPct(s, e) {
			const start = this.timeToPct(s);
			const end = this.timeToPct(e);
			return {
				value: end.value - start.value,
				outStart: start.outStart || end.outStart,
				outEnd: start.outEnd || end.outEnd,
			};
		},
		startChanged() {
			this.startMenu = false;
			if (!this.startDateM.equals(this.startM)) {
				this.refresh();
			}
		},
		filterChanged(newVal, oldVal) {
			if (newVal !== oldVal) {
				this.refresh();
			}
		},
		nowClick() {
			const now = this.dtnow();

			const start = now.set({ second: 0, millisecond: 0, minute: now.minute > 30 ? 30 : 0 });

			this.filter.dateIso = start.toISODate();
			this.filter.timeIso = start.toLocaleString(DateTime.TIME_24_SIMPLE);

			this.refresh();
		},
		move(newDate) {
			this.filter.dateIso = newDate.toISODate();
			this.filter.timeIso = newDate.toLocaleString(DateTime.TIME_24_SIMPLE);

			this.refresh();
		},
		statusMap(s) {
			switch (s) {
			case transactionStatus.Queued:
				return "start";
			case transactionStatus.Completed:
				return "end";
			case transactionStatus.Error:
			case transactionStatus.ErrorWillRetry:
				return "failed";
			case transactionStatus.Unknown:
				return "unknown";
			default:
				return "processing";
			}
		},
		goBack() {
			const newDate = this.startDateM.minus({ minutes: this.visibleTimeMin });
			this.move(newDate);
		},
		goForward() {
			const newDate = this.startDateM.plus({ minutes: this.visibleTimeMin });
			this.move(newDate);
		},
		startInterval() {
			this.intervalTimer = setInterval(this.refresh, 10000);
		},
		stopInterval() {
			clearInterval(this.intervalTimer);
		},
		refresh() {
			this.chartDay = this.startDateM;
			this.loadStatus();
		},
		loadStatus() {
			this.refreshing = true;

			const statusRequest = {
				start: this.startDateM.toISO(),
				length: this.filter.minutes,
				region: this.filter.region,
				offset: this.dtnow().offset,
			};

			api.status(statusRequest).then((r) => {
				this.requests = r.data.items;
				this.workers = r.data.workers;

				this.requests.forEach((i) => {
					i.created = this.dt(i.created);
					i.jobStarted = i.jobStarted ? this.dt(i.jobStarted) : null;
					i.jobFinished = i.jobFinished ? this.dt(i.jobFinished) : this.dtnow();
				});

				this.workers.forEach((w) => {
					w.created = this.dt(w.created);
					// TODO, color code offline
					w.offline = w.offline ? this.dt(w.offline) : this.dt(w.lastHeartbeat);
				});

				this.startM = this.startDateM;
				this.visibleTimeMin = this.filter.minutes;

				this.refreshing = false;
				this.loaded = true;
			}).catch((e) => {
				this.showError(e);
			});
		},
		yearDayClick(date) {
			const start = date.set({ hour: 12 });

			this.filter.dateIso = start.toISODate();
			this.filter.timeIso = start.toLocaleString(DateTime.TIME_24_SIMPLE);

			this.refresh();
		},
		hourClick(hour) {
			const start = this.startM.set({ hour: hour, minute: 0 });

			this.filter.dateIso = start.toISODate();
			this.filter.timeIso = start.toLocaleString(DateTime.TIME_24_SIMPLE);
			this.filter.minutes = 60;

			this.loadStatus();
		},
		requestClick(id) {
			this.trackingNumber = this.requests.filter(r => r.id === id)[0].trackingNumber;
		},
		viewTrackingNumber(trackingNumber) {
			this.trackingNumber = trackingNumber;
		},
		zoneChanged() {
			this.refresh();
		},
	},
};
</script>
